import type { AttachmentsContext } from "../prompt-input-context/AttachmentsContext";
import { PromptInputController } from "../prompt-input-context/PromptInputController";
import type { PromptInputControllerProps } from "../prompt-input-context/PromptInputControllerProps";
import { ProviderAttachmentsContext } from "../prompt-input-context/ProviderAttachmentsContext";
import type { FileUIPart } from "ai";
import { nanoid } from "nanoid";
import type { RefObject } from "react";
import { useCallback } from "react";
import { useEffect } from "react";
import { useMemo } from "react";
import { useRef } from "react";
import { useState } from "react";
import type { PromptInputProviderProps } from "./PromptInputProviderProps";

export function PromptInputProvider({
  initialInput: initialTextInput = "",
  dropZoneRef,
  children,
}: PromptInputProviderProps) {
  // ----- textInput state
  const [textInput, setTextInput] = useState(initialTextInput);
  const clearInput = useCallback(() => setTextInput(""), []);

  // ----- attachments state (global when wrapped)
  const [attachements, setAttachements] = useState<
    (FileUIPart & { id: string })[]
  >([]);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const openRef = useRef<() => void>(() => {});

  const appendAttachments = useCallback((files: File[] | FileList) => {
    const incoming = Array.from(files);
    if (incoming.length === 0) {
      return;
    }

    setAttachements((prev) =>
      prev.concat(
        incoming.map((file) => ({
          id: nanoid(),
          type: "file" as const,
          url: URL.createObjectURL(file),
          mediaType: file.type,
          filename: file.name,
        }))
      )
    );
  }, []);
  const addRef = useRef<(files: File[] | FileList) => void>(appendAttachments);
  const add = useCallback((files: File[] | FileList) => {
    addRef.current(files);
  }, []);

  const remove = useCallback((id: string) => {
    setAttachements((prev) => {
      const found = prev.find((f) => f.id === id);
      if (found?.url) {
        URL.revokeObjectURL(found.url);
      }
      return prev.filter((f) => f.id !== id);
    });
  }, []);

  const clear = useCallback(() => {
    // Don't revoke object URLs on bulk clear — consumers that already shipped
    // the file (e.g. as an optimistic chat message) may still need to render
    // from these URLs. The browser cleans them up on page unload anyway.
    // Per-attachment `remove()` keeps revoking since that's a user-initiated
    // pre-submit removal where nobody else holds the URL.
    setAttachements(() => []);
  }, []);

  const openFileDialog = useCallback(() => {
    openRef.current?.();
  }, []);

  // Drop-zone state + listener. Active only when the consumer passed a
  // `dropZoneRef`. The enter/leave counter handles the browser quirk where
  // `dragleave` fires every time the cursor crosses into a child element
  // — without the counter, the overlay would flicker on any descendant.
  const [isDraggingFiles, setIsDraggingFiles] = useState(false);
  const hasDropZone = !!dropZoneRef;
  useEffect(() => {
    const el = dropZoneRef?.current;
    if (!el) {
      return;
    }

    let depth = 0;
    const isFileDrag = (e: DragEvent) =>
      !!e.dataTransfer?.types?.includes("Files");

    const onDragEnter = (e: DragEvent) => {
      if (!isFileDrag(e)) {
        return;
      }
      e.preventDefault();
      depth++;
      if (depth === 1) {
        setIsDraggingFiles(true);
      }
    };
    const onDragOver = (e: DragEvent) => {
      if (isFileDrag(e)) {
        e.preventDefault();
      }
    };
    const onDragLeave = (e: DragEvent) => {
      if (!isFileDrag(e)) {
        return;
      }
      depth = Math.max(0, depth - 1);
      if (depth === 0) {
        setIsDraggingFiles(false);
      }
    };
    const onDrop = (e: DragEvent) => {
      if (!isFileDrag(e)) {
        return;
      }
      e.preventDefault();
      depth = 0;
      setIsDraggingFiles(false);
      if (e.dataTransfer?.files && e.dataTransfer.files.length > 0) {
        add(e.dataTransfer.files);
      }
    };

    el.addEventListener("dragenter", onDragEnter);
    el.addEventListener("dragover", onDragOver);
    el.addEventListener("dragleave", onDragLeave);
    el.addEventListener("drop", onDrop);
    return () => {
      el.removeEventListener("dragenter", onDragEnter);
      el.removeEventListener("dragover", onDragOver);
      el.removeEventListener("dragleave", onDragLeave);
      el.removeEventListener("drop", onDrop);
    };
  }, [dropZoneRef, add]);

  const attachments = useMemo<AttachmentsContext>(
    () => ({
      files: attachements,
      add,
      remove,
      clear,
      openFileDialog,
      fileInputRef,
      isDraggingFiles,
      hasDropZone,
    }),
    [
      attachements,
      add,
      remove,
      clear,
      openFileDialog,
      isDraggingFiles,
      hasDropZone,
    ]
  );

  const __registerFileInput = useCallback(
    (
      ref: RefObject<HTMLInputElement | null>,
      open: () => void,
      addFiles: (files: File[] | FileList) => void
    ) => {
      fileInputRef.current = ref.current;
      openRef.current = open;
      addRef.current = addFiles;
    },
    []
  );

  const controller = useMemo<PromptInputControllerProps>(
    () => ({
      textInput: {
        value: textInput,
        setInput: setTextInput,
        clear: clearInput,
      },
      attachments,
      __addAttachments: appendAttachments,
      __registerFileInput,
    }),
    [textInput, clearInput, attachments, appendAttachments, __registerFileInput]
  );

  return (
    <PromptInputController.Provider value={controller}>
      <ProviderAttachmentsContext.Provider value={attachments}>
        {children}
      </ProviderAttachmentsContext.Provider>
    </PromptInputController.Provider>
  );
}
